import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import LinkWrap from "../../components/LinkWrap/component";
import Ad from "../../components/Ad/component";
import MaybeError from "../../components/MaybeError/component";
import PersistentPlayerPlaceholder from "../../components/PersistentPlayer/components/Placeholder/component";
import { getIsLiveStream } from "../../utils/dataTransform/ContentItem/library";
import { FormattedAirDate } from "../../components/VideoUtils/components/FormattedDate/component";
import { getBaseCategoryByMediaItemCategories } from "../../utils/videoBaseCategories/library";
import { getFormattedDuration } from "../../utils/dateTime/library";
import { fetchVideoDetailPageData } from "./initData";
import { PAGE_COMPONENT_UNIVERSAL_CLASS_NAME } from "../../constants/page";
import PageAnalytics from "../../components/PageAnalytics/PageAnalytics";
import { TYPE_DETAIL } from "../../utils/analytics/constants";
import usePageFetch from "../../hooks/usePageFetch";
import IsMainContentContext from "../../context/IsMainContentContext";
import LayoutFactory from "../../components/LayoutFactory/component";
import { DEFAULT_SWIMLANE_LAYOUT } from "../../components/LayoutFactory/constants";
import { SectionHeading } from "../../components/Heading/component";
import { MLDRIVEN_ITEMS_ALGORITHM_TYPE } from "../FlexibleLandingPage/components/MLDrivenItemsWidget/constants";
import EngagementVideos from "./components/EngagementVideos/component";
import RelatedLinks from "../Detail/components/RelatedLinks/component";

const VideoDetailPage = (props) => {
    const dispatch = useDispatch();
    const currentClip = useSelector((state) => state.video.currentClip);
    const statusCode = useSelector((state) => state.app.statusCode);
    const trackingData = useSelector((state) => state.tracking.tracking);
    const moreFromBaseSectionItems = useSelector(
        (state) => state.video.moreFromBaseSection.items
    );
    const trendingItemsInStore = useSelector(
        (state) => state.video.trendingClips.items
    );
    const relatedStories = useSelector(
        (state) => state.videoDetail.relatedStories
    );

    const isLive = getIsLiveStream(currentClip.media?.streamType);

    const fetchData = () => {
        return dispatch(
            fetchVideoDetailPageData(null, {
                mediaId: props.match.params.mediaId,
            })
        );
    };

    usePageFetch(props.match, fetchData);

    const generateBaseCategoryLink = () => {
        if (!currentClip.sourceId) return null;

        let baseCategoryFullObj = getBaseCategoryByMediaItemCategories(
            currentClip.categories
        );

        if (baseCategoryFullObj)
            return (
                <LinkWrap
                    aria-label={`Content area ${currentClip.media?.contentArea}`}
                    url={`/player/${baseCategoryFullObj.path}`}
                    className={"video-base-category-link"}
                    data-testid="video-base-category-link"
                >
                    {baseCategoryFullObj.title}
                </LinkWrap>
            );

        if (currentClip.media?.contentArea) {
            return currentClip.media?.contentArea;
        }

        return null;
    };

    const getBigBox = () => {
        return <Ad type="bigbox-single" targeting={{ plc: "feed" }} />;
    };

    const getMoreFromBaseSectionSwimlane = () => {
        if (!moreFromBaseSectionItems?.length) return null;

        let baseCategory = currentClip.media?.contentArea;
        if (!baseCategory) {
            const baseCategoryFullObj = getBaseCategoryByMediaItemCategories(
                currentClip.categories
            );
            baseCategory = baseCategoryFullObj?.title ?? "CBC";
        }

        return (
            <div className="category-clips-swimlane">
                <SectionHeading text={`More from ${baseCategory}`} />
                <LayoutFactory
                    layout={DEFAULT_SWIMLANE_LAYOUT}
                    items={moreFromBaseSectionItems}
                    featureAttributionData={{ instance: baseCategory }}
                />
            </div>
        );
    };

    const getTrendingClips = () => {
        if (!trendingItemsInStore?.length) return;

        return (
            <div className="trending-clips-swimlane">
                <SectionHeading text={"Trending Now"} />
                <LayoutFactory
                    layout={DEFAULT_SWIMLANE_LAYOUT}
                    items={trendingItemsInStore}
                    featureAttributionData={{
                        instance: MLDRIVEN_ITEMS_ALGORITHM_TYPE.TRENDING,
                    }}
                />
            </div>
        );
    };

    const getRelatedStories = () => {
        if (!relatedStories.isLoaded || !relatedStories.items.length) return;
        return <RelatedLinks intlinks={relatedStories.items} />;
    };

    let formattedDuration = isLive
        ? "live"
        : getFormattedDuration(currentClip.media?.duration);

    const persistentPlayerConfig = {
        autoPlay: true,
        routeUpdate: true,
        useMediaAspectRatio: true,
    };

    const airDate = parseInt(currentClip.publishedAt);

    return (
        <MaybeError statusCode={statusCode}>
            <div
                className={classNames(
                    "videoDetailPage",
                    PAGE_COMPONENT_UNIVERSAL_CLASS_NAME
                )}
                data-testid="video-detail-page"
            >
                <div className="play-page two-columns">
                    <div className="play-page-main">
                        <div
                            className={`stage ${
                                !currentClip.sourceId ? "placeholder" : ""
                            }`}
                        >
                            {currentClip.sourceId ? (
                                <IsMainContentContext.Provider value={true}>
                                    <PersistentPlayerPlaceholder
                                        config={persistentPlayerConfig}
                                        mediaItem={currentClip}
                                        isMediaItemPlayable={true}
                                    />
                                </IsMainContentContext.Provider>
                            ) : (
                                <div className="placeholder-main-content"></div>
                            )}
                            <div className="details">
                                <div className="metadata">
                                    <h1 id="MainContentTitle">
                                        {currentClip.title}
                                    </h1>
                                    <ul className="factoids">
                                        <li id="StageTimestamp">
                                            <FormattedAirDate
                                                airDate={airDate}
                                                includeSeconds={true}
                                                isLive={isLive}
                                            />
                                        </li>
                                        <li>{generateBaseCategoryLink()}</li>
                                        <li>
                                            <span
                                                className={isLive ? "live" : ""}
                                            >
                                                <span className="a11y">
                                                    Duration{" "}
                                                </span>
                                                {formattedDuration}
                                            </span>
                                        </li>
                                        {currentClip.media?.hasCaptions && (
                                            <li className="cc-listitem">
                                                <svg
                                                    className="captions"
                                                    viewBox="0 0 24 24"
                                                    role="img"
                                                    aria-label="Close captions available"
                                                >
                                                    <path
                                                        d="M19,4 L5,4 C3.89,4 3,4.9 3,6 L3,18 C3,19.1 3.89,20 5,20 L19,20 C20.1,20 21,19.1 21,18 L21,6 C21,4.9 20.1,4 19,4 Z M11,11 L9.5,11 L9.5,10.5 L7.5,10.5 L7.5,13.5 L9.5,13.5 L9.5,13 L11,13 L11,14 C11,14.55 10.55,15 10,15 L7,15 C6.45,15 6,14.55 6,14 L6,10 C6,9.45 6.45,9 7,9 L10,9 C10.55,9 11,9.45 11,10 L11,11 Z M18,11 L16.5,11 L16.5,10.5 L14.5,10.5 L14.5,13.5 L16.5,13.5 L16.5,13 L18,13 L18,14 C18,14.55 17.55,15 17,15 L14,15 C13.45,15 13,14.55 13,14 L13,10 C13,9.45 13.45,9 14,9 L17,9 C17.55,9 18,9.45 18,10 L18,11 Z"
                                                        id="Shape"
                                                        fill="#546E7A"
                                                        fillRule="nonzero"
                                                    ></path>
                                                </svg>
                                            </li>
                                        )}
                                    </ul>
                                    <p id="MainContentDescription">
                                        {currentClip.description}
                                    </p>
                                </div>
                                <div className="ad-container">
                                    {getBigBox()}
                                </div>
                            </div>
                        </div>
                        {getRelatedStories()}
                    </div>
                    <div className="play-page-aside">
                        <EngagementVideos sourceId={currentClip.sourceId} />
                    </div>
                    {getMoreFromBaseSectionSwimlane()}
                    {getTrendingClips()}
                </div>
                <PageAnalytics dtmData={trackingData} type={TYPE_DETAIL} />
            </div>
        </MaybeError>
    );
};

export default VideoDetailPage;
